import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { ChangedFacetDetails } from 'src/components/organisms/Filter/types';
import type { SortOptionValue } from 'src/components/organisms/FilterMenu/types';
import { healthCheckProducts } from 'src/redux/products/thunks/healthCheckProducts';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import type { RequestProductsActionPayload } from 'src/redux/products/types';
import { BrowseDataAPIFilterUID } from 'src/services/browse-data-api/constants/BrowseDataAPIFilterUID';
import {
  setOptionActiveByValue,
  setOptionToActiveRemoveOthers,
  setOptionToActiveSetOthersToInactive,
} from './helpers/facetOptionHelpers';
import type { Facet } from './types/Facet';
import type { SortOption } from './types/SortOption';

export interface CanonicalFilters {
  categoryIDs?: number[];
  partnerShortcode?: string;
  searchTerm?: string;
}

export interface ProductsQueryState {
  canonicalFilters: CanonicalFilters;
  facets: Facet[];
  isModalOpen: boolean;
  page: number;
  sortOptions: SortOption[];
}

export const initialState: Readonly<ProductsQueryState> = {
  canonicalFilters: {},
  facets: [],
  isModalOpen: false,
  page: 1,
  sortOptions: [],
};

export const { actions, reducer } = createSlice({
  name: 'productsQuery',
  initialState,
  reducers: {
    closeModal: (state) => {
      state.isModalOpen = false;
    },
    openModal: (state) => {
      state.isModalOpen = true;
    },
    changeFacetOption: (state, action: PayloadAction<ChangedFacetDetails>) => {
      state.page = 1;
      const changedFacet = action.payload;

      state.facets = state.facets.map((facet) => {
        if (facet.key !== changedFacet.uid) {
          return facet;
        }

        if (facet.key === BrowseDataAPIFilterUID.Price) {
          return {
            ...facet,
            options: changedFacet.options,
          };
        }

        const { active, value } = changedFacet.options[0];

        switch (facet.type) {
          case 'tree':
            return {
              ...facet,
              options: setOptionToActiveRemoveOthers(facet.options, value),
            };
          case 'single':
            return {
              ...facet,
              options: setOptionToActiveSetOthersToInactive(facet.options, value),
            };
          case 'multi':
            return {
              ...facet,
              options: setOptionActiveByValue(facet.options, value, active),
            };
          default:
            return facet;
        }
      });
    },
    resetFacets: (state) => {
      state.page = 1;
      state.facets = state.facets.map((facet) => {
        const hasActiveOption = facet.options.some((option) => option.active);

        if (hasActiveOption) {
          return {
            ...facet,
            options: facet.options.map((option) => {
              if (option.active) {
                return {
                  ...option,
                  value: facet.type === 'price' ? null : option.value,
                  active: false,
                };
              }

              return option;
            }),
          };
        }

        return facet;
      });
    },
    changeSortOption: (state, action: PayloadAction<SortOptionValue>) => {
      state.page = 1;
      state.sortOptions = state.sortOptions.map((option) => {
        return {
          ...option,
          active: option.id === action.payload,
        };
      });
    },
    resetSortOptions: (state: ProductsQueryState) => {
      state.page = 1;
      state.sortOptions = state.sortOptions.map((option) => {
        return {
          ...option,
          active: false,
        };
      });
    },
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action): action is PayloadAction<RequestProductsActionPayload> =>
        action.type === healthCheckProducts.fulfilled.type ||
        action.type === requestProducts.fulfilled.type,
      (state, { payload }) => {
        if (payload.sortOptions) {
          state.sortOptions = payload.sortOptions;
        }

        if (payload.facets) {
          state.facets = payload.facets;
        }
      },
    );
  },
});
